// Conferência do endereço digitado contra a base de CEP (ViaCEP).
//
// Motivo: o pedido 29746 foi gravado com CEP 45100110 (Bahia) e endereço de São Paulo
// (Praça Pereira Coutinho, Vila Nova Conceição — CEP real 04510-010). O CEP errado tirou o
// pedido da faixa da frota própria e a NF saiu autorizada com ele. Nada na tela comparava o
// CEP com o endereço: a busca por CEP só SOBRESCREVIA os campos.
//
// A regra é aviso, não bloqueio: a base de CEP pode estar desatualizada (CEP novo) e as
// cidades de CEP único não têm rua nem bairro. Por isso campo vazio de qualquer lado não
// conta como divergência.
//
// Pura para ser testável sem renderizar tela (mesmo padrão de destAddress.ts). Há uma cópia
// equivalente em Marketing/CRMWebUI/src/utils/addressCepCheck.ts.

export interface CepAddress {
  uf: string;
  city: string;
  district: string;
  street: string;
}

export type AddressCepField = 'uf' | 'city' | 'district' | 'street';

export interface AddressDivergence {
  field: AddressCepField;
  typed: string;
  expected: string;
}

export const ADDRESS_CEP_FIELD_LABELS: Record<AddressCepField, string> = {
  uf: 'Estado',
  city: 'Cidade',
  district: 'Bairro',
  street: 'Rua',
};

/** Minúsculas, sem acento, sem pontuação e com espaços colapsados. */
export function normalizeAddressText(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const STREET_TYPE_ABBREVIATIONS: Record<string, string> = {
  r: 'rua',
  av: 'avenida',
  ave: 'avenida',
  pca: 'praca',
  pc: 'praca',
  al: 'alameda',
  tv: 'travessa',
  trav: 'travessa',
  estr: 'estrada',
  est: 'estrada',
  rod: 'rodovia',
  lgo: 'largo',
  lg: 'largo',
  pq: 'parque',
};

const STREET_TYPES = new Set([
  'rua', 'avenida', 'praca', 'alameda', 'travessa', 'estrada', 'rodovia', 'largo',
  'parque', 'viela', 'via', 'beco', 'servidao', 'ladeira',
]);

/**
 * Nome do logradouro pronto para comparar: corta o sufixo de faixa do ViaCEP
 * ("Rua X - até 999/1000"), expande a abreviação do tipo ("Pça." → praça) e remove o tipo,
 * para "Pça. Pereira Coutinho" e "Praça Pereira Coutinho" compararem iguais.
 */
export function normalizeStreetName(value: string | null | undefined): string {
  const withoutRange = (value ?? '').split(/\s+-\s+/)[0];
  // "Pç." normaliza para "pc", que está no mapa de abreviações.
  const words = normalizeAddressText(withoutRange).split(' ').filter(Boolean);
  if (words.length === 0) return '';
  const first = STREET_TYPE_ABBREVIATIONS[words[0]] ?? words[0];
  if (STREET_TYPES.has(first) && words.length > 1) {
    return words.slice(1).join(' ');
  }
  return words.join(' ');
}

// Igual, ou um contém o outro como sequência de PALAVRAS inteiras ("moema" ⊂ "moema passaros"),
// nunca como trecho solto de texto ("rio" não está contido em "riomar").
const containsWords = (haystack: string, needle: string): boolean =>
  ` ${haystack} `.includes(` ${needle} `);

const sameOrContained = (a: string, b: string): boolean =>
  !!a && !!b && (a === b || containsWords(a, b) || containsWords(b, a));

/**
 * Campos do endereço digitado que não conferem com o endereço do CEP, na ordem
 * Estado, Cidade, Bairro, Rua. Campo vazio no digitado ou no CEP não é comparado.
 */
export function compareAddressWithCep(typed: CepAddress, fromCep: CepAddress): AddressDivergence[] {
  const out: AddressDivergence[] = [];
  const push = (field: AddressCepField, equal: (t: string, e: string) => boolean) => {
    const t = (typed[field] ?? '').trim();
    const e = (fromCep[field] ?? '').trim();
    if (!t || !e) return;
    if (!equal(t, e)) out.push({ field, typed: t, expected: e });
  };
  push('uf', (t, e) => t.toUpperCase() === e.toUpperCase());
  push('city', (t, e) => normalizeAddressText(t) === normalizeAddressText(e));
  push('district', (t, e) => sameOrContained(normalizeAddressText(t), normalizeAddressText(e)));
  push('street', (t, e) => {
    const nt = normalizeStreetName(t);
    const ne = normalizeStreetName(e);
    return !!nt && !!ne && sameOrContained(nt, ne);
  });
  return out;
}

/** Preenche só os campos vazios com os dados do CEP. Nunca sobrescreve o digitado. */
export function fillEmptyFromCep(typed: CepAddress, fromCep: CepAddress): CepAddress {
  const pick = (field: AddressCepField) =>
    (typed[field] ?? '').trim() ? typed[field] : (fromCep[field] ?? '').trim() || typed[field];
  return { uf: pick('uf'), city: pick('city'), district: pick('district'), street: pick('street') };
}

/** Troca os campos divergentes pelos valores do CEP ("Usar endereço do CEP"). */
export function applyCepToDivergent(
  typed: CepAddress,
  fromCep: CepAddress,
  divergences: AddressDivergence[],
): CepAddress {
  const next = { ...typed };
  for (const d of divergences) {
    const value = (fromCep[d.field] ?? '').trim();
    if (value) next[d.field] = value;
  }
  return next;
}

/** "45100110" → "45100-110". Valor fora do padrão volta como veio. */
export function formatCepDisplay(cep: string | null | undefined): string {
  const digits = (cep ?? '').replace(/\D/g, '');
  return digits.length === 8 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : (cep ?? '');
}
